/*
 * ux-experiment-tracking
 *
 * Intelligems custom events for A/B test metrics, configured from snippets/ux-experiment-tracking.liquid
 * (Theme settings → Experiment trackingᵘˣ). Two jobs:
 *
 * 1. Add-to-cart per product. Rather than parse each add-to-cart response — the theme alone has three
 *    shapes (product-form.js, ux-buy-buttons.js, complementary-products.js) and apps such as Selleasy
 *    may use XHR or the Storefront API — we watch for any cart-mutating request, re-read /cart.js and
 *    compare per-product quantities with the last snapshot. An increase after an add-type request is
 *    an add to cart. Failed adds and product-form.js's quantity-0 retry after a 422 leave the cart
 *    unchanged, so they track nothing. The snapshot is kept in sessionStorage and compared against the
 *    Liquid-rendered cart on every page load, which catches adds nothing here saw (a full-page POST to
 *    /cart/add, or a mechanism we did not anticipate).
 *
 * 2. Traffic source. One bucket per session, on the landing page, from UTMs, click IDs and the
 *    referrer. Intelligems can filter experiment results by any custom event.
 *
 * Intelligems events carry no properties, so every product and bucket has its own event ID, created in
 * Intelligems → Events Manager. Pushes queue on window.igEvents until the Intelligems script loads.
 */
(() => {
  const script = document.currentScript;
  if (!script || window.uxExperimentTracking) return;
  window.uxExperimentTracking = true;

  const readJSON = (value, fallback) => {
    try {
      return JSON.parse(value) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // Storage blocked (private mode, disabled cookies): tracking degrades, the page does not.
      }
    }
  };

  const atcEvents = readJSON(script.dataset.atcEvents, {}); // { [productId]: { event, name } }
  const sourceEvents = readJSON(script.dataset.sourceEvents, {}); // { [bucket]: event }
  const debug = script.dataset.debug === 'true';

  if (debug) console.info('[ux-exp] loaded', { atcEvents, sourceEvents });

  const SNAPSHOT_KEY = 'ux-exp:cart';
  const SESSION_KEY = 'ux-exp:source';

  const track = (eventId, label) => {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: eventId });
    if (debug) console.info('[ux-exp]', `${label} → ${eventId}`);
  };

  /* ---------------------------------------------------------------- Add to cart */

  const toQuantities = (lines) =>
    lines.reduce((quantities, [productId, quantity]) => {
      quantities[productId] = (quantities[productId] ?? 0) + Number(quantity);
      return quantities;
    }, {});

  let snapshot = readJSON(storage.get(SNAPSHOT_KEY), null);

  const commit = (next, trackIncreases) => {
    if (trackIncreases && snapshot) {
      const added = Object.entries(next)
        .filter(([productId, quantity]) => quantity > (snapshot[productId] ?? 0))
        .map(([productId, quantity]) => ({ product_id: Number(productId), quantity_added: quantity - (snapshot[productId] ?? 0) }));

      added.forEach(({ product_id: productId }) => {
        const atc = atcEvents[productId];
        if (atc) track(atc.event, `atc:${atc.name}`);
      });

      // Reusable hook for any future tracking (GA4, Meta…) without another fetch wrapper.
      if (added.length) document.dispatchEvent(new CustomEvent('ux:cart-add', { detail: { items: added } }));
    }

    snapshot = next;
    storage.set(SNAPSHOT_KEY, JSON.stringify(next));
  };

  const nativeFetch = window.fetch;
  const cartJsUrl = `${window.routes?.cart_url ?? '/cart'}.js`;

  // Sequential, so several adds in a row are compared against each other rather than double-counted.
  let queue = Promise.resolve();
  const syncCart = (kind) => {
    queue = queue
      .then(() => nativeFetch.call(window, cartJsUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }))
      .then((response) => response.json())
      .then((cart) => commit(toQuantities(cart.items.map((item) => [item.product_id, item.quantity])), kind === 'add'))
      .catch(() => {});
  };

  const CART_ADD_PATH = /\/cart\/add(\.js)?$/;
  const CART_CHANGE_PATH = /\/cart\/(change|update|clear)(\.js)?$/;
  const STOREFRONT_API_PATH = /\/api\/[^/]+\/graphql(\.json)?$/;

  // 'add' | 'change' | null. Only 'add' tracks; 'change' just keeps the snapshot honest so a remove
  // followed by a re-add still reads as an increase.
  const classify = (url, body) => {
    const { pathname } = new URL(url, window.location.origin);
    if (CART_ADD_PATH.test(pathname)) return 'add';
    if (CART_CHANGE_PATH.test(pathname)) return 'change';
    if (STOREFRONT_API_PATH.test(pathname) && typeof body === 'string') {
      if (/cartLinesAdd|cartCreate/.test(body)) return 'add';
      if (/cartLines(Update|Remove)|cartBuyerIdentityUpdate/.test(body)) return 'change';
    }
    return null;
  };

  window.fetch = function (...args) {
    const request = nativeFetch.apply(this, args);
    try {
      const [input, init] = args;
      const kind = classify(input instanceof Request ? input.url : String(input), init?.body);
      if (kind) request.then((response) => response.ok && syncCart(kind)).catch(() => {});
    } catch {
      // Never let tracking interfere with the request itself.
    }
    return request;
  };

  const xhrUrls = new WeakMap();
  const { open: nativeOpen, send: nativeSend } = XMLHttpRequest.prototype;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    xhrUrls.set(this, String(url));
    return nativeOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    try {
      const kind = classify(xhrUrls.get(this), body);
      if (kind) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) syncCart(kind);
        });
      }
    } catch {
      // As above.
    }
    return nativeSend.call(this, body);
  };

  // Anything added since the last page we saw (and not caught above) shows up as an increase here.
  commit(toQuantities(readJSON(script.dataset.cart, [])), true);

  /* --------------------------------------------------------------- Traffic source */

  const SEARCH_HOST = /(^|\.)(google|bing|yahoo|duckduckgo|ecosia|baidu|yandex|qwant|startpage)\.[a-z.]+$/;
  const SOCIAL_HOST =
    /(^|\.)(facebook|fb|instagram|twitter|linkedin|lnkd|pinterest|tiktok|youtube|reddit|snapchat|threads)\.[a-z.]+$|^(t\.co|x\.com)$/;
  // Our own pages and checkout / payment round-trips are not a new landing.
  const IGNORED_HOST = /(^|\.)(amazfit\.com|myshopify\.com|shopify\.com|shop\.app|paypal\.com|klarna\.com|clearpay\.co\.uk)$/;

  const SEARCH_SOURCE = /(^|[^a-z])(google|bing|yahoo|duckduckgo|ecosia|microsoft)([^a-z]|$)/;
  const SOCIAL_SOURCE = /(^|[^a-z])(facebook|fb|instagram|ig|meta|twitter|x|linkedin|pinterest|tiktok|youtube|reddit|snapchat|threads)([^a-z]|$)/;
  const PAID_MEDIUM = /^(cpc|ppc|cpm|cpv|paid|ads?|display|paid[_-]?search|paid[_-]?social|paidsocial|social[_-]?paid|sem)$/;

  // Checked in order; the first match wins. Edit here to change how sources are grouped.
  const SOURCE_RULES = [
    ['affiliate', ({ params, medium }) => params.has('irclickid') || /affiliate|partner/.test(medium)],
    [
      'paid_search',
      ({ params, medium, source }) =>
        ['gclid', 'gbraid', 'wbraid', 'msclkid'].some((key) => params.has(key)) ||
        /paid[_-]?search|^sem$/.test(medium) ||
        (PAID_MEDIUM.test(medium) && SEARCH_SOURCE.test(source))
    ],
    [
      'paid_social',
      ({ params, medium, source, referrerHost }) =>
        params.has('ttclid') ||
        /paid[_-]?social|paidsocial|social[_-]?paid/.test(medium) ||
        (PAID_MEDIUM.test(medium) && (SOCIAL_SOURCE.test(source) || SOCIAL_HOST.test(referrerHost)))
    ],
    ['email', ({ medium, source }) => /e-?mail|newsletter/.test(medium) || /klaviyo/.test(source)],
    ['sms', ({ medium, source }) => /^(sms|text|mms)$/.test(medium) || /(^|[^a-z])sms([^a-z]|$)/.test(source)],
    ['organic_search', ({ referrerHost }) => SEARCH_HOST.test(referrerHost)],
    [
      'organic_social',
      ({ params, medium, source, referrerHost }) =>
        params.has('fbclid') || SOCIAL_HOST.test(referrerHost) || /^social$|organic[_-]?social/.test(medium) || SOCIAL_SOURCE.test(source)
    ],
    ['referral', ({ source, referrerHost }) => Boolean(source || referrerHost)],
    ['direct', () => true]
  ];

  const classifySource = () => {
    const params = new URLSearchParams(window.location.search);
    const medium = (params.get('utm_medium') ?? '').toLowerCase();
    const source = (params.get('utm_source') ?? '').toLowerCase();
    let referrerHost = '';
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
    } catch {
      // Malformed referrer: treat as none.
    }

    const internal = referrerHost && (referrerHost === window.location.hostname || IGNORED_HOST.test(referrerHost));
    if (internal && !params.has('utm_source')) return null;

    const context = { params, medium, source, referrerHost: internal ? '' : referrerHost };
    return SOURCE_RULES.find(([, matches]) => matches(context))[0];
  };

  if (Object.keys(sourceEvents).length && !storage.get(SESSION_KEY)) {
    try {
      const bucket = classifySource();
      if (bucket) {
        storage.set(SESSION_KEY, bucket);
        if (sourceEvents[bucket]) track(sourceEvents[bucket], `src:${bucket}`);
      }
    } catch {
      // Never let tracking break the page.
    }
  }
})();
